import { cn } from "@app/ui";
import type { ReactNode } from "react";
import { TASK_COLOR_PALETTE } from "./task-colors.js";

interface Props {
  /** Currently-selected value, for the ring highlight. Omit to disable the ring. */
  value?: string | null;
  onSelect: (value: string | null) => void;
  /** Container layout classes (e.g. "grid grid-cols-7 gap-1.5" or "flex flex-wrap gap-2"). */
  className?: string;
  /** Extra controls rendered after the palette (e.g. the dialog's custom picker). */
  children?: ReactNode;
  /** Optional transform applied to each swatch's displayed color (e.g. `pastel`
   *  in event mode). Does not change the value passed to `onSelect`. */
  tint?: ((hex: string) => string) | undefined;
}

/** The task-color palette as a row of swatch buttons. Shared by the task form
 *  and the context menu so the swatch rendering lives in one place. */
export function ColorSwatchGrid({ value, onSelect, className, children, tint }: Props) {
  return (
    <div className={className ?? "grid grid-cols-7 gap-1.5"}>
      {TASK_COLOR_PALETTE.map((c) => (
        <button
          key={c.label}
          type="button"
          title={c.label}
          aria-label={c.label}
          aria-pressed={value !== undefined && value === c.value}
          onClick={() => onSelect(c.value)}
          className={cn(
            "h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 active:scale-95",
            c.value === null ? "relative overflow-hidden border-border bg-background" : "border-transparent",
            value !== undefined && value === c.value && "ring-2 ring-offset-1 ring-foreground",
          )}
          style={c.value !== null ? { backgroundColor: tint ? tint(c.value) : c.value } : undefined}
        >
          {c.value === null && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-muted-foreground" aria-hidden>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
                <line x1="4" y1="4" x2="20" y2="20" />
              </svg>
            </span>
          )}
        </button>
      ))}
      {children}
    </div>
  );
}
